import React, { useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { IProjectView, IUser } from '../../api-client/autogenerated';
import { addOrModifyUsersOfProject } from '../../models/api/project';
import { getUserState } from '../../features/user/selectors';
import { fetchUser } from '../../features/user/actions';
import { getProjectState } from '../../features/project/selectors';
import { PersonAdd } from '@material-ui/icons';
import { reloadProject } from '../../features/project/actions';
import { isProjectArchived } from '../../scripts/permission-utils';

/**
 * True when the calling user is a subscriber admin of the project's subscriber AND does
 * not yet have a `project_user` row for the project. Exported so callers can decide
 * whether to render surrounding chrome (e.g. a wrapping Card).
 */
export const isEligibleToJoinProjectAsSubscriberAdmin = (
  user: IUser | undefined | null,
  project: IProjectView | undefined | null,
): boolean => {
  if (!user?.id || !project?.id || !project.subscriberId) return false;
  // Joining adds a project_user row, which an archived project no longer accepts.
  if (isProjectArchived(project)) return false;
  if (!user.adminOfSubscriberId) return false;
  if (user.adminOfSubscriberId !== project.subscriberId) return false;
  return !user.projectUserList?.some((pu) => pu.projectId === project.id);
};

export default function JoinProjectAsSubscriberAdminButton() {
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);

  const [isJoining, setIsJoining] = useState(false);

  if (!project || !isEligibleToJoinProjectAsSubscriberAdmin(user, project)) return null;

  const handleJoin = async () => {
    setIsJoining(true);
    try {
      await addOrModifyUsersOfProject(project.id, [
        { userId: user.id, securityGroupId: undefined },
      ]);
      // Refresh redux user so projectUserList includes the new row; the eligibility
      // check above will then return false and the button will unmount automatically.
      await dispatch(fetchUser());
      await dispatch(reloadProject());
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handleJoin}
      disabled={isJoining}
      startIcon={isJoining ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
      style={{ whiteSpace: 'nowrap' }}
    >
      Add me to this project
    </Button>
  );
}
